package com.islamicmedia.shared.domain.model

import kotlinx.serialization.Serializable

/** One "What's inside" line on a kid's result, e.g. 🙊 "Uses bad language". Server copy: server/.../models/Models.kt. */
@Serializable
data class KidsPoint(
    val emoji: String,
    val text: String,
    /** "high" | "medium" | "low". */
    val severity: String = "medium",
    /** Concern key ("violence", "music", ...). Null from a server that predates the age bands. */
    val key: String? = null,
    /** 7 and under: the simple words shown (and read aloud). Null from an older server: [shortLabel] is used. */
    val young: String? = null,
    /** 8 and over: one or two sentences about this concern. Null from an older server. */
    val detail: String? = null
)

/**
 * The ONLY result shape Kids Mode ever shows. Built server-side from the adult analysis
 * (KidsResultService): fixed verdict + phrases, plus three short tips. Kids Mode never holds an
 * [AnalysisResult], so no adult text can reach a child's screen by a UI slip.
 */
@Serializable
data class KidsResult(
    /** "go" | "ask" | "no". */
    val verdict: String,
    val headline: String,
    val points: List<KidsPoint> = emptyList(),
    val goodStuff: String? = null,
    val islamicTip: String,
    val growingTip: String,
    val title: String = "",
    val mediaType: String = "",
    val coverUrl: String? = null,
    /** 7 and under: the stricter verdict. Null from an older server (see [verdictFor]). */
    val youngVerdict: String? = null,
    /** 8 and over, on a MAYBE: "Talk to your parent about…". Null from an older server. */
    val talkAbout: String? = null,
    /** 8 and over: "Islam teaches", written for this title. Null = show [islamicTip]. */
    val islamAbout: String? = null,
    /** 8 and over: "Positive aspects", written for this title. Null from an older server or when none were written. */
    val positives: List<String>? = null
) {
    val kidsVerdict: KidsVerdict get() = KidsVerdict.from(verdict)

    /**
     * The verdict this band sees. 7 and under uses the server's stricter [youngVerdict]; a result from an older
     * server has none, so the same rule is applied here from the points (anything high, or fighting at medium,
     * is a NO; a YES with anything at medium is a MAYBE).
     */
    fun verdictFor(band: KidsAgeBand): KidsVerdict {
        if (band == KidsAgeBand.OLDER) return kidsVerdict
        youngVerdict?.let { return KidsVerdict.from(it) }
        if (kidsVerdict == KidsVerdict.NO) return KidsVerdict.NO
        if (points.any { it.severity == "high" }) return KidsVerdict.NO
        if (points.any { it.severity == "medium" && it.emoji.startsWith("👊") }) return KidsVerdict.NO
        if (kidsVerdict == KidsVerdict.GO && points.any { it.severity == "medium" }) return KidsVerdict.ASK
        return kidsVerdict
    }

    /** The "What's inside" lines this band sees: 7 and under gets at most three, with no repeated words. */
    fun pointsFor(band: KidsAgeBand): List<KidsPoint> =
        if (band == KidsAgeBand.OLDER) points
        else points.distinctBy { it.label(band) }.take(YOUNG_MAX_POINTS)

    /**
     * What "Read it to me" says (7 and under): the big word, the headline, and the things inside, the way the
     * screen shows them. Built only from the result's own approved text. [talk] says the "talk to a grown-up"
     * line too: leave it out while that card is still under its bubbles, so the voice doesn't give it away.
     */
    fun spokenText(band: KidsAgeBand = KidsAgeBand.YOUNG, talk: Boolean = true): String {
        val v = verdictFor(band)
        val head = when (v) {
            KidsVerdict.GO -> "Good to go!"
            KidsVerdict.ASK -> "Ask a grown-up first."
            KidsVerdict.NO -> "Not for you."
        }
        val inside = pointsFor(band).map { it.label(band) }
        return buildString {
            if (title.isNotBlank()) append(title).append(". ")
            append(v.word.lowercase().replaceFirstChar { it.uppercase() }).append("! ").append(head)
            if (inside.isNotEmpty()) append(" It has: ").append(inside.joinToString(", ").lowercase()).append(".")
            else if (v == KidsVerdict.GO) append(" Nothing to worry about!")
            if (v != KidsVerdict.GO && talk) append(" Talk to a grown-up about this one.")
        }
    }

    companion object {
        const val YOUNG_MAX_POINTS = 3
    }
}

/** Who KIDZONE is set up for. The parent picks it on the setup screen; the child can't change it. */
enum class KidsAgeBand(val wire: String) {
    /** 7 and under: very simple words, big pictures, camera first, "Read it to me". */
    YOUNG("young"),
    /** 8 and over: a sentence or two per concern, a calmer look, "talk to your parent about…", safer picks on a NO. */
    OLDER("older");

    companion object {
        fun parse(value: String?): KidsAgeBand? = entries.firstOrNull { it.wire == value }
    }
}

/** A "Try one of these instead" title (8 and over, on a NO): only its name and picture. */
@Serializable
data class KidsAlternative(
    val title: String,
    val mediaType: String,
    val year: String? = null,
    val coverUrl: String? = null
)

enum class KidsVerdict(val face: String, /** The words beside the traffic light; also read out by "Read it to me" and screen readers. */ val word: String) {
    GO("😊", "GO"), ASK("🤔", "SLOW DOWN"), NO("✋", "STOP");

    companion object {
        fun from(value: String): KidsVerdict = when (value) {
            "go" -> GO
            "ask" -> ASK
            // Unknown → the most cautious answer.
            else -> NO
        }
    }
}

/** The `code` / `error` the server refuses a kids request with when KIDZONE isn't switched on for the account. */
const val KIDS_NOT_ALLOWED_CODE = "kids_not_allowed"

/** Why a kid can't get an answer right now. Every one of them renders as "Ask a grown-up". */
enum class KidsBlock {
    /** Not subscribed / out of credits / signed out / session lost / KIDZONE not switched on for the account. */
    GROWN_UP_NEEDED,
    /** Fair-use daily cap or the hourly rate limit. */
    DAILY_LIMIT,
    /**
     * No longer produced: whether KIDZONE is on is now decided per account by the server (the
     * entitlement's `kids_mode`), and a backend or account without it is [GROWN_UP_NEEDED]. KEPT
     * because it is persisted in a [KidsScan] on devices that already hold one, and removing the
     * value would make that stored list undecodable.
     */
    SERVER_WITHOUT_KIDS
}

enum class KidsScanStatus { CHECKING, DONE, FAILED, BLOCKED }

/**
 * One title a kid checked — what the "My scans" grid shows. Persisted (small) so it survives an
 * app restart mid-check, and so the parent's Your Analyses can pick the titles up on PIN exit.
 */
@Serializable
data class KidsScan(
    val id: String,
    val title: String,
    val mediaType: MediaType,
    val year: String? = null,
    val info: String? = null,
    val author: String? = null,
    val coverUrl: String? = null,
    val status: KidsScanStatus = KidsScanStatus.CHECKING,
    val result: KidsResult? = null,
    val block: KidsBlock? = null,
    val journeyId: String? = null,
    val startedAt: Long = 0L,
    /** Already queued for the parent's Your Analyses on an earlier exit, so a later exit doesn't queue it again. */
    val exported: Boolean = false
)

/** Progress of one Kids Mode analysis, as parsed from the kid-filtered SSE stream. */
sealed class KidsProgress {
    /** Still working. [fraction] is a rough 0..1 when known. */
    data class Working(val fraction: Float?) : KidsProgress()
    data class Done(val result: KidsResult) : KidsProgress()
    /** [notAllowed] = the server said KIDZONE is not switched on for this account (`kids_not_allowed`). */
    data class Blocked(val block: KidsBlock, val notAllowed: Boolean = false) : KidsProgress()
    /** [recoverable] = the pipeline may still finish server-side — try the cache before giving up. */
    data class Failed(val recoverable: Boolean) : KidsProgress()
}

/** One read of a kid's answer from the cache (/api/cached-result with kids=true). */
sealed class KidsCacheRead {
    data class Ready(val result: KidsResult) : KidsCacheRead()
    /** Not there yet (still running, or a network blip) — ask again later. */
    object NotReady : KidsCacheRead()
    /** The server answered with the ADULT shape: it doesn't have Kids Mode and never will answer. */
    object Unsupported : KidsCacheRead()
    /** The server refused: KIDZONE is not switched on for this account (403 `kids_not_allowed`). */
    object NotAllowed : KidsCacheRead()
}

/**
 * The word shown next to a "What's inside" picture: one word (two at most), not a sentence. Keyed
 * by the server's fixed emoji per concern, so old cached results and the live server need no
 * change; anything unknown falls back to the server's sentence.
 */
/** The words next to a "What's inside" picture for this band: 7 and under gets the simpler [KidsPoint.young] when the server sent it. */
fun KidsPoint.label(band: KidsAgeBand): String =
    if (band == KidsAgeBand.YOUNG) young ?: shortLabel() else shortLabel()

fun KidsPoint.shortLabel(): String = when {
    emoji.startsWith("🙊") -> "Bad words"
    emoji.startsWith("👊") -> "Fighting"
    emoji.startsWith("🙈") -> "Romance"
    emoji.startsWith("👗") -> "Inappropriate clothing"
    emoji.startsWith("🚫") -> "Haram"
    emoji.startsWith("🍺") -> "Drinking"
    emoji.startsWith("🎰") -> "Gambling"
    emoji.startsWith("🪄") -> "Magic"
    emoji.startsWith("😤") -> "Rudeness"
    emoji.startsWith("🕌") -> "Against Islam"
    emoji.startsWith("👀") -> "Family rule"
    emoji.startsWith("🎵") -> "Music"
    emoji.startsWith("⚠") -> "Other"
    else -> text
}
